import { RWA_COIN_ICON, TOKENIZATION_TYPE_IDS } from "~/consts";
import { ImageLoader } from "~/components/ImageLoader";
import { Link } from "~/components/Link";
import { ColumnMetadata, ColumnTypes } from "~/components/DataGrid";
import { GigatableRow, GigatableColumns } from "./types";
import { Icon } from "~/components/Icon";
import { NetworkBadge } from "~/modules/assets/components/AssetTable/NetworkBadge";
import { GlobalAssetTableEvents } from "~/modules/analytics/events/global_asset_table";
import { TokenizationTypeBadge } from "~/components/TokenizationTypeBadge";

// // // //

export const RANK_COLUMN: ColumnMetadata<GigatableRow, GigatableColumns> = {
    label: "#",
    slug: GigatableColumns.rank,
    type: ColumnTypes.number,
    width: 80,
    maxWidth: 80,
    sticky: {
        left: 0,
        isLast: false,
    },
    cellValue: (row: GigatableRow) => {
        return row.rank;
    },
};

export const NAME_COLUMN: ColumnMetadata<GigatableRow, GigatableColumns> = {
    label: "Name",
    slug: GigatableColumns.name,
    sortsOn: GigatableColumns.name,
    type: ColumnTypes.string,
    width: 350,
    sticky: {
        left: 80,
        isLast: true,
    },
    render: (item: GigatableRow) => {
        return (
            <Link
                href={item.url}
                variant="default"
                className="flex items-center gap-1.5"
                analyticsLabel={GlobalAssetTableEvents.ClickAssetLink}
                analyticsEventProperties={{
                    asset_ticker: item.ticker,
                }}
            >
                <ImageLoader
                    src={item.icon_url || RWA_COIN_ICON}
                    alt={`${item.name} Icon`}
                    fallbackSrc={RWA_COIN_ICON}
                    className="h-6 w-6 min-w-6 rounded-full"
                />
                <div className="flex flex-col dg-overflow ml-2">
                    <div className="dg-overflow">{item.name}</div>
                    {item.ticker && (
                        <div className="text-sm text-slate-500">
                            {item.ticker}
                        </div>
                    )}
                </div>
            </Link>
        );
    },
};

export const TOKENIZATION_TYPE_COLUMN: ColumnMetadata<
    GigatableRow,
    GigatableColumns
> = {
    label: "Asset Type",
    slug: GigatableColumns.transferable,
    sortsOn: GigatableColumns.transferable,
    type: ColumnTypes.string,
    width: 150,
    maxWidth: 150,
    render: (item: GigatableRow) => {
        return (
            <TokenizationTypeBadge
                className="w-full flex justify-center items-center"
                tokenization_type_id={
                    item.distributed
                        ? TOKENIZATION_TYPE_IDS.OnChainDistributed
                        : TOKENIZATION_TYPE_IDS.OnChainRepresented
                }
                size="sm"
            />
        );
    },
};

export const VALUE_COLUMN: ColumnMetadata<GigatableRow, GigatableColumns> = {
    slug: GigatableColumns.value,
    label: "Total Value",
    type: ColumnTypes.dollar,
    sortsOn: GigatableColumns.value,
    width: 160,
    maxWidth: 160,
    cellValue: (item) => {
        return item.value;
    },
};

export const APY_7D_COLUMN: ColumnMetadata<GigatableRow, GigatableColumns> = {
    slug: GigatableColumns.apy_7d,
    label: "7D APY",
    type: ColumnTypes.percent,
    sortsOn: GigatableColumns.apy_7d,
    loginRequired: true,
    width: 120,
    maxWidth: 120,
    cellValue: (item) => {
        return item.apy_7d;
    },
};

export const PCT_CHANGE_7D_COLUMN: ColumnMetadata<
    GigatableRow,
    GigatableColumns
> = {
    slug: GigatableColumns.pct_change_7d,
    label: "7D Change",
    type: ColumnTypes.percentChange,
    sortsOn: GigatableColumns.pct_change_7d,
    loginRequired: true,
    width: 130,
    maxWidth: 130,
    cellValue: (item) => {
        return item.pct_change_7d;
    },
};

export const PCT_CHANGE_30D_COLUMN: ColumnMetadata<
    GigatableRow,
    GigatableColumns
> = {
    slug: GigatableColumns.pct_change_30d,
    label: "30D Change",
    type: ColumnTypes.percentChange,
    sortsOn: GigatableColumns.pct_change_30d,
    loginRequired: true,
    width: 135,
    maxWidth: 135,
    cellValue: (item) => {
        return item.pct_change_30d;
    },
};

export const PROTOCOL_COLUMN: ColumnMetadata<GigatableRow, GigatableColumns> = {
    label: "Platform",
    slug: GigatableColumns.protocol,
    sortsOn: GigatableColumns.protocol,
    type: ColumnTypes.string,
    width: 170,
    render: (row: GigatableRow) => {
        return (
            <Link
                href={row.protocol.url}
                analyticsLabel={GlobalAssetTableEvents.ClickProtocolLink}
                analyticsEventProperties={{
                    protocol: row.protocol.name,
                }}
                className="flex items-center gap-2"
                variant="default"
            >
                <ImageLoader
                    src={row.protocol.icon_url || RWA_COIN_ICON}
                    alt={`${row.protocol.name} Icon`}
                    fallbackSrc={RWA_COIN_ICON}
                    className="h-5 w-5 min-w-5 rounded-full"
                />
                <div className="dg-overflow">{row.protocol.name}</div>
            </Link>
        );
    },
};

export const ASSET_CLASS_COLUMN: ColumnMetadata<
    GigatableRow,
    GigatableColumns
> = {
    slug: GigatableColumns.asset_class,
    sortsOn: GigatableColumns.asset_class,
    label: "Asset Class",
    type: ColumnTypes.string,
    width: 170,
    maxWidth: 250,
    render: (item) => {
        if (!item.asset_class.url) {
            return (
                <div className="flex items-center gap-2 text-secondary-foreground overflow-hidden">
                    <Icon className="h-3.5" icon={item.asset_class.icon} />
                    <span className="dg-overflow">{item.asset_class.name}</span>
                </div>
            );
        }

        return (
            <Link
                href={item.asset_class.url}
                analyticsLabel={GlobalAssetTableEvents.ClickAssetClassLink}
                analyticsEventProperties={{
                    asset_class: item.asset_class.name,
                }}
                variant="default"
                className="flex items-center gap-2 overflow-hidden"
            >
                <Icon className="h-3.5" icon={item.asset_class.icon} />
                <span className="dg-overflow">{item.asset_class.name}</span>
            </Link>
        );
    },
};

export const NETWORKS_COLUMN: ColumnMetadata<GigatableRow, GigatableColumns> = {
    slug: GigatableColumns.networks,
    sortsOn: GigatableColumns.networks,
    label: "Networks",
    type: ColumnTypes.string,
    width: 180,
    maxWidth: 225,
    render: (item) => {
        const allNetworks = item.networks;

        if (allNetworks.length === 1) {
            const network = allNetworks[0];

            return (
                <Link
                    variant="default"
                    className="py-0.5 px-0.5 flex items-center"
                    analyticsLabel={GlobalAssetTableEvents.ClickNetworkLink}
                    analyticsEventProperties={{
                        network: network.slug,
                    }}
                    href={network.url}
                >
                    <NetworkBadge
                        network={{
                            slug: network.slug,
                            name: network.name,
                            icon_url: network.icon_url,
                        }}
                        enableTooltip={false}
                    />
                    <p className="ml-2 dg-overflow">{network.name}</p>
                </Link>
            );
        }

        return (
            <div className="py-0.5 px-0.5 isolate flex -space-x-2 overflow-hidden h-full items-center w-full">
                {allNetworks.map((n) => (
                    <Link
                        key={n.slug}
                        href={n.url}
                        analyticsLabel={GlobalAssetTableEvents.ClickNetworkLink}
                        analyticsEventProperties={{
                            network: n.slug,
                        }}
                    >
                        <NetworkBadge network={n} />
                    </Link>
                ))}
            </div>
        );
    },
};
